/** SettingValue.key for the first-backfill window in months (default 3). */
export const SYNC_START_MONTHS_SETTING_KEY = 'metric.syncStartMonths';

/** Default first-backfill window when the setting is unset. */
export const DEFAULT_SYNC_START_MONTHS = 3;

/** Supported syncStartMonths range (months). */
export const MIN_SYNC_START_MONTHS = 1;
/** Ten years — long enough to cover a full Oura account history. */
export const MAX_SYNC_START_MONTHS = 120;

/** Approximate milliseconds per month for first-backfill lookback. */
export const MS_PER_SYNC_MONTH = (365.25 / 12) * 24 * 60 * 60 * 1000;

/** One labeled slider mark for the sync lookback control. */
export type SyncStartMonthsSliderMark = {
  readonly value: number;
  readonly label: string;
};

/**
 * Clamps a syncStartMonths preference into the supported range [1, 120].
 * @param value Raw setting value
 * @returns Clamped month count
 */
export function clampSyncStartMonths(value: number): number {
  if (!Number.isFinite(value)) {
    return DEFAULT_SYNC_START_MONTHS;
  }
  return Math.min(MAX_SYNC_START_MONTHS, Math.max(MIN_SYNC_START_MONTHS, Math.round(value)));
}

/**
 * Resolves syncStartMonths from a stored setting value or the default.
 * @param storedValue Raw setting value decode result, if any
 * @returns Clamped month count for sync lookback
 */
export function resolveSyncStartMonths(storedValue: number | null | undefined): number {
  if (storedValue === null || storedValue === undefined) {
    return DEFAULT_SYNC_START_MONTHS;
  }
  return clampSyncStartMonths(storedValue);
}

/**
 * Converts a syncStartMonths preference into a first-backfill lookback duration.
 * @param months Month count (clamped to [1, 120])
 * @returns Lookback duration in milliseconds
 */
export function syncStartMonthsToLookbackMs(months: number): number {
  return Math.round(clampSyncStartMonths(months) * MS_PER_SYNC_MONTH);
}

/**
 * Converts an oldest sample day into a syncStartMonths lookback, rounding up so the first day is
 * not clipped, then clamping to [1, 120].
 * @param oldestDay Inclusive ISO date of the earliest sample.
 * @param now Reference instant (typically now).
 * @returns Clamped month count covering `oldestDay`.
 */
export function syncStartMonthsFromOldestDay(oldestDay: string, now: Date = new Date()): number {
  const oldestMs = Date.parse(`${oldestDay}T00:00:00.000Z`);
  if (!Number.isFinite(oldestMs)) {
    return DEFAULT_SYNC_START_MONTHS;
  }
  const months = Math.ceil((now.getTime() - oldestMs) / MS_PER_SYNC_MONTH);
  return clampSyncStartMonths(months);
}

/**
 * Sparse slider marks for the lookback control (monthly ends plus yearly ticks).
 * @param maxMonths Slider maximum (clamped).
 * @returns Marks labeled with month counts.
 */
export function syncStartMonthsSliderMarks(
  maxMonths: number,
): readonly SyncStartMonthsSliderMark[] {
  const clampedMax = clampSyncStartMonths(maxMonths);
  const marks: SyncStartMonthsSliderMark[] = [
    { value: MIN_SYNC_START_MONTHS, label: String(MIN_SYNC_START_MONTHS) },
  ];
  for (let months = 12; months < clampedMax; months += 12) {
    marks.push({ value: months, label: String(months) });
  }
  if (clampedMax !== MIN_SYNC_START_MONTHS) {
    marks.push({ value: clampedMax, label: String(clampedMax) });
  }
  return marks;
}
